import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import {
  Plus,
  UserPlus,
  Phone,
  Mail,
  MapPin,
  Clock,
  Trash2,
  CheckCircle2,
  XCircle,
  Hourglass,
  Wallet,
  X,
} from "lucide-react";
import { api } from "../lib/api";

const STATUS_META = {
  pending: { iconKey: "status_pending", icon: Hourglass, color: "text-slate1", bg: "bg-cream2 border-ink/15" },
  confirmed: { iconKey: "status_confirmed", icon: CheckCircle2, color: "text-gold", bg: "bg-gold/10 border-gold/30" },
  rejected: { iconKey: "status_rejected", icon: XCircle, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase rounded-sm border ${m.bg} ${m.color}`}>
      <Icon size={11} /> {t(`contacts.${m.iconKey}`)}
    </span>
  );
}

export default function Contatti() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";
  const [contacts, setContacts] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    property_address: "",
    estimated_time: "subito",
    notes: "",
  });
  const [clientConsent, setClientConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const TIME_OPTIONS = [
    { value: "subito", label: t("contacts.time_subito") },
    { value: "entro_3_mesi", label: t("contacts.time_3m") },
    { value: "3_6_mesi", label: t("contacts.time_3_6m") },
    { value: "oltre_6_mesi", label: t("contacts.time_over_6m") },
  ];

  async function load() {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        api.get("/contacts"),
        api.get("/rewards/wallet"),
      ]);
      setContacts(c.data);
      setWallet(w.data);
    } catch {
      toast.error(t("contacts.load_error"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const pending = contacts.filter((c) => c.status === "pending").length;
    const confirmed = contacts.filter((c) => c.status === "confirmed").length;
    const rejected = contacts.filter((c) => c.status === "rejected").length;
    const earned = confirmed * 100;
    const expected = pending * 100;
    return { pending, confirmed, rejected, earned, expected, total: contacts.length };
  }, [contacts]);

  function reset() {
    setForm({ first_name: "", last_name: "", phone: "", email: "", property_address: "", estimated_time: "subito", notes: "" });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.email.trim() || !form.property_address.trim()) {
      toast.error(t("contacts.submit_required_fields"));
      return;
    }
    if (!clientConsent) {
      toast.error(t("contacts.submit_consent_required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      toast.success(t("contacts.submit_success"));
      setModalOpen(false);
      reset();
      setClientConsent(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contacts.submit_error"));
    } finally {
      setSubmitting(false);
    }
  }

  async function removeContact(c) {
    if (!window.confirm(t("contacts.delete_confirm", { name: `${c.first_name} ${c.last_name}` }))) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      toast.success(t("contacts.delete_success"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contacts.delete_error"));
    }
  }

  return (
    <div className="pt-28 pb-20 bg-cream min-h-[100svh]" data-testid="contatti-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("contacts.section")}</div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light text-ink">{t("contacts.title")}</h1>
            <p className="mt-3 text-slate1 max-w-2xl">
              {t("contacts.subtitle_pre")}<span className="text-ink font-medium">{t("contacts.subtitle_em1")}</span>{t("contacts.subtitle_pre2")}<span className="text-ink font-medium">{t("contacts.subtitle_em2")}</span>{t("contacts.subtitle_post")}
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift"
            data-testid="add-contact-btn"
          >
            <Plus size={14} /> {t("contacts.new_contact")}
          </button>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="contacts-stats">
          <div className="bg-white border border-ink/10 p-5 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("contacts.wallet_active")}</div>
            <div className="mt-1 font-display text-3xl text-ink inline-flex items-center gap-2"><Wallet size={20} className="text-gold" /> € {wallet?.balance?.toLocaleString("it-IT") ?? "0"}</div>
          </div>
          <div className="bg-white border border-ink/10 p-5 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("contacts.confirmed_contacts")}</div>
            <div className="mt-1 font-display text-3xl text-ink">{stats.confirmed}</div>
            <div className="text-xs text-slate1 mt-0.5">{t("contacts.credited", { amount: stats.earned.toLocaleString("it-IT") })}</div>
          </div>
          <div className="bg-white border border-ink/10 p-5 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("contacts.pending")}</div>
            <div className="mt-1 font-display text-3xl text-ink">{stats.pending}</div>
            <div className="text-xs text-slate1 mt-0.5">{t("contacts.potential", { amount: stats.expected.toLocaleString("it-IT") })}</div>
          </div>
          <div className="bg-white border border-ink/10 p-5 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("contacts.total_loaded")}</div>
            <div className="mt-1 font-display text-3xl text-ink">{stats.total}</div>
            <div className="text-xs text-slate1 mt-0.5">{t("contacts.rejected_sub", { count: stats.rejected })}</div>
          </div>
        </div>

        {/* Contacts list */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-ink">{t("contacts.list_title")}</h2>
            <div className="text-xs text-slate1">{t("contacts.records", { count: contacts.length })}</div>
          </div>
          {loading ? (
            <div className="text-sm text-slate1">{t("contacts.loading")}</div>
          ) : contacts.length === 0 ? (
            <div className="bg-white border border-dashed border-ink/20 rounded-sm p-10 text-center" data-testid="contacts-empty">
              <UserPlus size={28} className="mx-auto text-slate1" />
              <div className="mt-4 font-display text-xl text-ink">{t("contacts.empty_title")}</div>
              <p className="mt-2 text-sm text-slate1">{t("contacts.empty_sub")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map((c) => {
                const timeLabel = TIME_OPTIONS.find((o) => o.value === c.estimated_time)?.label || c.estimated_time_label;
                return (
                  <div key={c.id} className="bg-white border border-ink/10 rounded-sm p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid={`contact-${c.id}`}>
                    <div className="lg:col-span-4">
                      <div className="font-display text-xl text-ink">{c.first_name} {c.last_name}</div>
                      <div className="mt-2 text-sm text-slate1 inline-flex items-center gap-1.5"><Mail size={12} /> {c.email}</div>
                      <div className="mt-1 text-sm text-slate1 inline-flex items-center gap-1.5"><Phone size={12} /> {c.phone}</div>
                    </div>
                    <div className="lg:col-span-4">
                      <div className="text-xs text-slate1 inline-flex items-center gap-1.5"><MapPin size={12} /> {t("contacts.immobile")}</div>
                      <div className="text-sm text-ink mt-0.5">{c.property_address}</div>
                      {c.notes && <div className="text-xs text-slate1 italic mt-1.5">&ldquo;{c.notes}&rdquo;</div>}
                    </div>
                    <div className="lg:col-span-2">
                      <div className="text-xs text-slate1 inline-flex items-center gap-1.5"><Clock size={12} /> {t("contacts.furnishing_time")}</div>
                      <div className="text-sm text-ink mt-0.5">{timeLabel}</div>
                      <div className="text-[11px] text-slate1 mt-0.5">{new Date(c.created_at).toLocaleDateString(dateLocale)}</div>
                    </div>
                    <div className="lg:col-span-2 flex flex-col lg:items-end gap-2">
                      <StatusBadge status={c.status} />
                      <div className="font-display text-lg text-ink">€ {c.credit_amount.toLocaleString("it-IT")}</div>
                      {c.status === "pending" && (
                        <button
                          onClick={() => removeContact(c)}
                          className="text-xs text-slate1 hover:text-rose-600 inline-flex items-center gap-1"
                          data-testid={`contact-delete-${c.id}`}
                        >
                          <Trash2 size={12} /> {t("contacts.delete")}
                        </button>
                      )}
                      {c.status === "rejected" && c.rejection_reason && (
                        <div className="text-[11px] text-rose-700 italic">{c.rejection_reason}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal new contact */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalOpen(false)} data-testid="contact-modal">
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-cream border border-ink/15 rounded-sm w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl text-ink">{t("contacts.modal_title")}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-slate1 hover:text-ink" aria-label={t("contacts.cancel")}>
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.first_name")} *</label>
                <input
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-first-name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.last_name")} *</label>
                <input
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-last-name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.phone")} *</label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.email")} *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-email"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.address")} *</label>
                <input
                  required
                  value={form.property_address}
                  onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                  placeholder={t("contacts.address_placeholder")}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-address"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.time_lbl")} *</label>
                <select
                  value={form.estimated_time}
                  onChange={(e) => setForm({ ...form, estimated_time: e.target.value })}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="contact-time"
                >
                  {TIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">{t("contacts.notes")}</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={t("contacts.notes_placeholder")}
                  className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm resize-none"
                  data-testid="contact-notes"
                />
              </div>
            </div>

            <div className="mt-5 bg-cream2 border border-ink/10 rounded-sm p-3 text-xs text-slate1">
              <Trans i18nKey="contacts.info_box" />
            </div>

            <div className="mt-3 bg-rose-50 border-l-4 border-rose-600 rounded-sm p-3 text-xs text-rose-800 leading-relaxed" data-testid="contact-redflag">
              <span className="font-semibold uppercase tracking-[0.15em] text-[10px]">{t("contacts.redflag_title")}</span><br />
              <Trans i18nKey="contacts.redflag_body" />
            </div>

            <label className="mt-4 flex items-start gap-3 cursor-pointer text-xs text-slate1 leading-relaxed bg-cream2 border border-ink/15 rounded-sm p-3" data-testid="contact-consent-label">
              <input
                type="checkbox"
                required
                checked={clientConsent}
                onChange={(e) => setClientConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-ink shrink-0"
                data-testid="contact-consent"
              />
              <span>
                <span className="text-ink font-medium">{t("contacts.consent_title")}</span><br />
                {t("contacts.consent_body")}
              </span>
            </label>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-5 py-2.5 border border-ink/20 text-[11px] tracking-[0.2em] uppercase rounded-sm text-ink hover:bg-ink hover:text-cream transition-colors"
              >
                {t("contacts.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50"
                data-testid="contact-submit"
              >
                {submitting ? t("contacts.saving") : t("contacts.save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
